/**
 * GoogleDriveService.js — OAuth + automatic backup upload to Google Drive
 * 
 * Tokens stored in FIXARR-CENTRAL Supabase (gdrive_tokens table).
 * 
 * Flow:
 * 1. User clicks "Conectar Google Drive" → redirects to Google OAuth
 * 2. Google redirects back to /vault?code=XXX → exchanges code for tokens
 * 3. Tokens saved to gdrive_tokens table in FIXARR-CENTRAL
 * 4. Each backup → uploads ZIP to "FIXARR_Backups" folder in Drive
 */
import { createClient } from '@supabase/supabase-js';

const CLIENT_ID = '47685312658-1opb31s0o5f9a237h3rukmudm7ofb1er.apps.googleusercontent.com';
// 30/10/2026 (auditoría G9): el secreto OAuth de Google iba escrito aquí y salía en el JS público de
// roadmap.fixarr.es. Un secreto de cliente no puede ir en una página: se quita y la conexión con Drive
// queda desactivada hasta que el intercambio de códigos se haga en el servidor (docs/PLAN_ROTACION_CLAVES_2026-10-29.md, §2.6.3).
// No rompe nada que funcionase: gdrive_tokens (CENTRAL) no admite escrituras con la clave pública y
// tenía 0 filas, así que la conexión ya no llegaba a guardarse.
const CLIENT_SECRET = null;
const DRIVE_DESACTIVADO = 'La copia automática en Google Drive está desactivada por seguridad. Descarga la copia y súbela a mano.';
const REDIRECT_URI = 'https://roadmap.fixarr.es/vault';
const SCOPE = 'https://www.googleapis.com/auth/drive.file';

const FOLDER_NAME = 'FIXARR_Backups';
const TOKEN_ID = 'admin';

// FIXARR-CENTRAL (same credentials as BackupService.js)
const CENTRAL_URL = 'https://amqfnlxsymugnrspdgul.supabase.co';
const CENTRAL_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFtcWZubHhzeW11Z25yc3BkZ3VsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg0OTcwNjgsImV4cCI6MjA1NDA3MzA2OH0.AtDjXf5bN2zfIkfxEW7CtROCldAg4Qp2hK5L4VjEQXY';

const centralClient = createClient(CENTRAL_URL, CENTRAL_ANON_KEY);

// ── In-memory cache to avoid repeated DB reads per session ──
let _cachedTokens = null;

// ── Token Management (Supabase) ──

async function getTokens() {
    if (_cachedTokens) return _cachedTokens;

    try {
        const { data, error } = await centralClient
            .from('gdrive_tokens')
            .select('*')
            .eq('id', TOKEN_ID)
            .maybeSingle();

        if (error || !data) return null;
        _cachedTokens = data;
        return data;
    } catch {
        return null;
    }
}

async function saveTokens(tokens) {
    const row = {
        id: TOKEN_ID,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token || null,
        expires_in: tokens.expires_in || 3600,
        scope: tokens.scope || SCOPE,
        saved_at: Date.now(),
        updated_at: new Date().toISOString(),
    };

    // Upsert: insert if not exists, update if exists
    const { error } = await centralClient
        .from('gdrive_tokens')
        .upsert(row, { onConflict: 'id' });

    if (error) {
        console.error('[GoogleDrive] Error saving tokens to Supabase:', error);
        throw error;
    }

    _cachedTokens = row;
}

export async function clearTokens() {
    await centralClient
        .from('gdrive_tokens')
        .delete()
        .eq('id', TOKEN_ID);
    _cachedTokens = null;
    // Also clean up any legacy localStorage tokens
    try { localStorage.removeItem('fixarr_gdrive_tokens'); } catch { /* ignore */ }
}

export async function isConnected() {
    const tokens = await getTokens();
    return !!(tokens && tokens.access_token);
}

// ── OAuth Flow ──

export function connectGoogleDrive() {
    if (!CLIENT_SECRET) { window.alert(DRIVE_DESACTIVADO); return; }
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        response_type: 'code',
        scope: SCOPE,
        access_type: 'offline',
        prompt: 'consent',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

export async function handleOAuthCallback(code) {
    if (!CLIENT_SECRET) return { success: false, error: DRIVE_DESACTIVADO };
    try {
        const response = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                code,
                client_id: CLIENT_ID,
                client_secret: CLIENT_SECRET,
                redirect_uri: REDIRECT_URI,
                grant_type: 'authorization_code',
            }),
        });

        if (!response.ok) {
            const err = await response.text();
            throw new Error(`Token exchange failed: ${err}`);
        }

        const tokens = await response.json();
        await saveTokens(tokens);
        return { success: true };
    } catch (e) {
        console.error('[GoogleDrive] OAuth callback error:', e);
        return { success: false, error: e.message };
    }
}

async function refreshTokenIfNeeded() {
    if (!CLIENT_SECRET) throw new Error(DRIVE_DESACTIVADO);
    const tokens = await getTokens();
    if (!tokens) throw new Error('No Google Drive tokens found');

    // Check if token is expired (saved_at is in ms, expires_in in seconds)
    const expiresAt = (tokens.saved_at || 0) + (tokens.expires_in || 3600) * 1000;
    if (Date.now() < expiresAt - 60000) {
        return tokens.access_token; // Still valid (with 1min buffer)
    }

    if (!tokens.refresh_token) {
        throw new Error('No refresh token — reconnect Google Drive');
    }

    // Refresh the token
    const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            refresh_token: tokens.refresh_token,
            grant_type: 'refresh_token',
        }),
    });

    if (!response.ok) {
        await clearTokens();
        throw new Error('Token refresh failed — reconnect Google Drive');
    }

    const newTokens = await response.json();
    await saveTokens({
        ...tokens,
        access_token: newTokens.access_token,
        expires_in: newTokens.expires_in,
    });
    return newTokens.access_token;
}

// ── Drive Operations ──

async function getOrCreateBackupFolder(accessToken) {
    // Search for existing folder
    const searchParams = new URLSearchParams({
        q: `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
        fields: 'files(id, name)',
    });

    const searchRes = await fetch(`https://www.googleapis.com/drive/v3/files?${searchParams}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (searchRes.ok) {
        const data = await searchRes.json();
        if (data.files && data.files.length > 0) {
            return data.files[0].id;
        }
    }

    // Create folder
    const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: FOLDER_NAME,
            mimeType: 'application/vnd.google-apps.folder',
        }),
    });

    if (!createRes.ok) {
        throw new Error(`Failed to create Drive folder: ${createRes.status}`);
    }

    const folder = await createRes.json();
    return folder.id;
}

export async function uploadBackup(blob, filename) {
    try {
        const accessToken = await refreshTokenIfNeeded();
        const folderId = await getOrCreateBackupFolder(accessToken);

        // Use multipart upload
        const metadata = {
            name: filename,
            parents: [folderId],
            mimeType: 'application/zip',
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', blob, filename);

        const uploadRes = await fetch(
            'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink,size',
            {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: form,
            }
        );

        if (!uploadRes.ok) {
            const err = await uploadRes.text();
            throw new Error(`Upload failed (${uploadRes.status}): ${err}`);
        }

        const file = await uploadRes.json();
        console.log(`[GoogleDrive] Uploaded: ${file.name} (${file.id})`);
        return {
            success: true,
            fileId: file.id,
            fileName: file.name,
            webViewLink: file.webViewLink,
            size: file.size,
        };
    } catch (e) {
        console.error('[GoogleDrive] Upload error:', e);
        return { success: false, error: e.message };
    }
}
